import { useStore } from '@nanostores/react';
import { action, computed, atom } from 'nanostores';
import type { Account, User } from '@/features/auth';
import { PlanFeature } from '@/features/auth';
import { snakeCase } from 'lodash-es';
import { permissionsForRole } from '@customerio/hydra-permissions/roles';
import type { Permission } from '@customerio/hydra-permissions';
import { getLimitValue } from '@/utils/plan-features';
import { AppBundle } from '@/utils/app-bundles';
import { UserMasqPermission } from '@customerio/hydra-permissions/masq';

export interface AugmentedUser extends User {
  permissions: Record<Permission, boolean>;

  // masq permissions computed properties
  isSuperUser: boolean;
  canReadOnlyMasq: boolean;
  canReadWriteMasq: boolean;
  noMasqAccess: boolean;
  masqLevelNotSet: boolean;
}

interface PlanFeatureInfo {
  enabled: boolean;
  showPremiumLabel: boolean;
  limit: number | false;
}

export interface AugmentedAccount extends Account {
  // active products as flags for easy access
  activeProducts: Record<AppBundle, boolean>;
  // plan features computed property
  planFeatures: Record<PlanFeature, PlanFeatureInfo>;
}

export const accountStore = atom<AugmentedAccount | null>(null);
export const setAccountStore = action(
  accountStore,
  'setAccountStore',
  (store: typeof accountStore, account: Account) => {
    // Enables premium label for premium trial users
    const premiumLabelEnabled = account.premium_trial === true && account.premium === false;
    const gatedFeatures = account.gated_features ?? {};

    const planFeatures: Partial<Record<PlanFeature, PlanFeatureInfo>> = {};
    Object.values(PlanFeature).forEach((key) => {
      const feature = gatedFeatures[snakeCase(key)];
      const limit = getLimitValue(feature);
      const enabled = feature !== undefined; // if a feature is present on the gatedFeatures object, it is enabled.
      planFeatures[key] = {
        enabled,
        showPremiumLabel: !enabled || premiumLabelEnabled,
        limit,
      };
    });

    const activeProducts = Object.values(AppBundle).reduce((activeProductsMap, app) => {
      activeProductsMap[app] = account?.product_activations?.includes(app) ?? false;
      return activeProductsMap;
    }, {} as Record<AppBundle, boolean>);

    const augmentedAccount: AugmentedAccount = {
      ...account,
      activeProducts,
      planFeatures: planFeatures as Record<PlanFeature, PlanFeatureInfo>,
    };

    store.set(augmentedAccount);
    return store.get();
  },
);

export const userStore = atom<AugmentedUser | null>(null);
export const setUserStore = action(userStore, 'setUserStore', (store: typeof userStore, user: User) => {
  const masqPermissions = user?.masq_permissions ?? UserMasqPermission.NoMasqAccess;

  const augmentedUser: AugmentedUser = {
    ...user,
    permissions: permissionsForRole(user.role),
    isSuperUser: masqPermissions === UserMasqPermission.SuperUser,
    canReadOnlyMasq: masqPermissions === UserMasqPermission.ReadOnly || masqPermissions === UserMasqPermission.Redacted,
    canReadWriteMasq: masqPermissions === UserMasqPermission.ReadWrite,
    noMasqAccess: masqPermissions === UserMasqPermission.NoMasqAccess || masqPermissions == null,
    masqLevelNotSet: masqPermissions === UserMasqPermission.MasqLevelNotSet,
  };
  store.set(augmentedUser);
  return store.get();
});

export interface AuthStore {
  account: AugmentedAccount | null;
  user: AugmentedUser | null;
}

export const authStore = computed([accountStore, userStore], (account, user) => {
  return {
    account,
    user,
  };
});

export enum AccountFeatureFlag {
  Whatsapp = 'whatsapp',
  StoreAnonEvents = 'store_anon_events',
  JavascriptWebhook = 'javascript_webhook',
  LegacyCustomSmtpBilling = 'legacy_custom_smtp_billing',
  SubscriptionCenterM1 = 'subscription_center_m1',
  MultiLanguageSettings = 'multi_language_settings',
  ExitOnlyAfterMessage = 'exit_only_after_message',
  CdpEnabled = 'cdp_enabled',
  ParcelAmp = 'parcel_amp',
  CdpDataCatalogEnabled = 'cdp_data_catalog_enabled',
  ParcelInHydra = 'parcel_in_hydra',
  InAppLegacyEditor = 'in_app_legacy_editor',
  InteractiveDemoWorkspace = 'interactive_demo_workspace', // DEPRECATED
  SampleWorkspace = 'sample_workspace',
  TrialRebootLite = 'trial_reboot_lite',
  TourtipAdventuresV1 = 'tourtip_adventures_v1',
  FlagCDPUIConsolidation = 'cdp_ui_consolidation',
  AiCopilot = 'ai-copilot',
  AiEnabled = 'ai_enabled',
}

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  subscription_center_m1: false, // enables milestone 0 and milestone 1 features of the subscription center
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  cdp_enabled: false, // disables cdp
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  parcel_in_hydra: false, // enables Parcel in Hydra
  in_app_legacy_editor: false, // enables the legacy in-app editor
  interactive_demo_workspace: false, // SOON-TO-BE DEPRECATED, can be removed after backend migration, use sample_workspace instead
  sample_workspace: false, // enables the sample workspace
  trial_reboot_lite: false, // enables the trial reboot lite work
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed
  rocket_v1: false, // enables the user-workspace guided tour V1
  tourtip_adventures_v1: false,
  'ai-copilot': false, // enables the AI Copilot feature
  ai_enabled: false, // enables the AI features in the app
});

export const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

export const hasFeatureFlag = (flag: FeatureFlag | string) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag as FeatureFlag] ?? false;

  const account = authStore.get()?.account;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};
